<!-- 📁 src/components/CompetitionDetails.vue -->
<template>
  <div>
    <div class="competition-details-grid mb-2">
      <!-- Columna 1 -->
      <div class="details-column">
        <div class="details-column-inner">
          <DetailItem
              icon="fa-solid fa-calendar-alt"
              :label="$t('competitions.view.date')"
              :value="formattedDate"
          />
          <DetailItem
              icon="fa-solid fa-clock"
              :label="$t('competitions.view.time')"
              :value="formattedTime"
          />
          <DetailItem
              icon="fa-solid fa-flag"
              :label="$t('competitions.view.country')"
              :value="competition.competitions_info?.country?.name"
          />
          <DetailItem
              icon="fa-solid fa-gavel"
              :label="$t('competitions.view.rule')"
              :value="competition.rule?.name"
          />

          <div class="organizer-section">
            <a v-if="competition.organizer?.name" href="#" class="link-plain organizer-link" @click.prevent="viewOrganizer(competition.organizer?.id)">
              <DetailItem
                  icon="fa-solid fa-user-tie"
                  :label="$t('competitions.view.organizer')"
                  :value="competition.organizer?.name"
              />
            </a>
            <button
                v-if="canOpenCoOrganizerModal"
                type="button"
                class="co-organizer-add-btn"
                :disabled="isBusy"
                :title="coOrganizerAddButtonTitle"
                @click="openCoOrganizerModal"
            >
              <i class="fa-solid fa-plus"></i>
            </button>
          </div>

          <div v-if="showCoOrganizerBlock" class="co-organizers-inline">
            <div class="co-organizers-head">
              <span class="co-organizers-title">{{ $t('competitions.view.co_organizers') }}</span>
              <span class="co-organizers-counter">{{ visibleCoOrganizerSlots }}/2</span>
            </div>

            <p v-if="coOrganizerFeedback.message" :class="['co-organizer-feedback', `is-${coOrganizerFeedback.type}`]">
              {{ coOrganizerFeedback.message }}
            </p>

            <div v-if="visibleCoOrganizers.length" class="co-organizer-list">
              <div v-for="coOrganizer in visibleCoOrganizers" :key="coOrganizer.id" class="co-organizer-row">
                <div class="co-organizer-content">
                  <button
                      type="button"
                      class="co-organizer-name-btn"
                      @click="viewOrganizer(coOrganizer.user?.id || coOrganizer.user_id)"
                  >
                    {{ fullName(coOrganizer.user, coOrganizer.mail_inscription) }}
                  </button>
                  <span :class="['co-organizer-status', statusBadgeClass(coOrganizer.status_id)]">
                    <i :class="statusIcon(coOrganizer.status_id)"></i>
                    {{ coOrganizerStatusLabel(coOrganizer.status_id) }}
                  </span>
                </div>

                <div class="co-organizer-actions">
                  <button
                      v-if="canSendInvitation(coOrganizer)"
                      type="button"
                      class="mini-action send"
                      :disabled="isBusy"
                      title="Enviar invitación"
                      @click="updateCoOrganizerStatus(coOrganizer, 2)"
                  >
                    <i class="fa-solid fa-paper-plane"></i>
                  </button>
                  <button
                      v-if="canAcceptInvitation(coOrganizer)"
                      type="button"
                      class="mini-action approve"
                      :disabled="isBusy"
                      title="Aceptar invitación"
                      @click="updateCoOrganizerStatus(coOrganizer, 3)"
                  >
                    <i class="fa-solid fa-check"></i>
                  </button>
                  <button
                      v-if="canApproveCoOrganizer(coOrganizer)"
                      type="button"
                      class="mini-action approve"
                      :disabled="isBusy"
                      title="Aprobar co-organizador"
                      @click="updateCoOrganizerStatus(coOrganizer, 4)"
                  >
                    <i class="fa-solid fa-user-check"></i>
                  </button>
                  <button
                      v-if="canRejectInvitation(coOrganizer)"
                      type="button"
                      class="mini-action reject"
                      :disabled="isBusy"
                      :title="Number(coOrganizer.status_id) === 4 ? 'Revocar co-organizador' : 'Rechazar invitación'"
                      @click="updateCoOrganizerStatus(coOrganizer, 5)"
                  >
                    <i :class="Number(coOrganizer.status_id) === 4 ? 'fa-solid fa-user-slash' : 'fa-solid fa-xmark'"></i>
                  </button>
                </div>
              </div>
            </div>

            <p v-else class="co-organizer-empty">
              {{ $t('competitions.view.co_organizers_empty') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Columna 2 -->
      <div class="details-column">
        <div class="details-column-inner">
          <DetailItem
              :icon="competition.type_id === 1 ? 'fa-solid fa-globe' : 'fa-solid fa-location-dot'"
              :label="$t('competitions.view.kind')"
              :value="competition.type?.name"
          />
          <DetailItem
              :icon="competition.is_open ? 'fa-solid fa-lock-open' : 'fa-solid fa-lock'"
              :label="$t('competitions.view.register')"
              :value="formattedIsOpen"
          />
          <DetailItem
              :icon="competition.is_vs ? 'fa-solid fa-users' : 'fa-solid fa-user'"
              :label="$t('competitions.view.modality')"
              :value="formattedIsVs"
          />
          <DetailItem
              icon="fa-solid fa-star-half-stroke"
              :label="$t('competitions.view.is_qualification_dual')"
              :value="formattedQualification"
          />
          <DetailItem
              :icon="controlIconCategory(competition.competitions_info?.category)"
              :label="$t('competitions.view.category')"
              :value="controlValueCategory(competition.competitions_info?.category)"
          />
          <DetailItem
              :icon="controlIconGender(competition.competitions_info?.gender)"
              :label="$t('competitions.view.gender')"
              :value="controlValueGender(competition.competitions_info?.gender)"
          />
        </div>
      </div>
    </div>

    <div v-if="showLiveQrBlock" class="live-qr-strip">
      <div :class="['live-qr-strip-actions', { 'has-multiple': hasMultipleLiveActions }]">
        <a
            v-if="liveFollowAvailable"
            :href="livePublicUrl"
            class="live-qr-action live-qr-follow-link"
            target="_blank"
            rel="noopener noreferrer"
            :title="liveFollowButtonTitle"
        >
          <i class="fa-solid fa-tower-broadcast"></i>
          <span>{{ $t('competitions.view.live_follow') }}</span>
        </a>

        <button
            v-else-if="showLiveQrButton"
            type="button"
            class="live-qr-action live-qr-follow-link"
            disabled
            :title="liveFollowButtonTitle"
        >
          <i class="fa-solid fa-tower-broadcast"></i>
          <span>{{ $t('competitions.view.live_follow') }}</span>
        </button>

        <button
            v-if="showLiveQrButton"
            type="button"
            class="live-qr-action live-qr-trigger"
            :disabled="liveQrLoading || !canGenerateLiveQr"
            :title="liveQrButtonTitle"
            @click="openLiveQrModal"
        >
          <i class="fa-solid fa-qrcode"></i>
          <span>{{ liveQrLoading ? $t('competitions.view.live_qr_generating') : $t('competitions.view.live_qr_button') }}</span>
        </button>

        <button
            v-if="showCompetitionStatisticsButton"
            type="button"
            class="live-qr-action live-competition-stats-trigger"
            :disabled="competitionStatisticsLoading"
            :title="competitionStatisticsButtonTitle"
            @click="openCompetitionStatisticsModal"
        >
          <i class="fa-solid fa-chart-column"></i>
          <span>{{ competitionStatisticsLoading ? tOr('profile.stats.loading', 'Loading statistics...') : tOr('menu.statistics', 'Statistics') }}</span>
        </button>
      </div>
    </div>

    <div v-if="isCoOrganizerModalVisible" class="modal-backdrop" @click.self="closeCoOrganizerModal">
      <div class="modal-content co-organizer-modal">
        <h3>Añadir co-organizador</h3>

        <div class="search-bar form-group">
          <div class="input-group">
            <span class="input-group-text"><i class="fa fa-search"></i></span>
            <input
                v-model="organizerSearchQuery"
                type="text"
                class="form-control"
                placeholder="Buscar organizer por nombre, usuario o email"
                @input="onOrganizerSearchInput"
            />
          </div>
        </div>

        <div class="selected-organizer-box">
          <label class="selected-organizer-label">Seleccionado</label>
          <div class="selected-organizer-input">
            <input
                type="text"
                class="form-control"
                :value="selectedOrganizerLabel"
                placeholder="Selecciona un organizer del listado"
                readonly
            />
            <button
                v-if="selectedShortOrganizer"
                type="button"
                class="clear-selection-btn"
                :disabled="isBusy"
                @click="clearSelectedOrganizer"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        <p v-if="shortOrganizersError" class="co-organizer-feedback is-error">{{ shortOrganizersError }}</p>

        <div class="short-organizers-list">
          <button
              v-for="organizer in filteredShortOrganizers"
              :key="organizer.id"
              type="button"
              :class="['short-organizer-item', { selected: selectedShortOrganizer && Number(selectedShortOrganizer.id) === Number(organizer.id) }]"
              @click="selectShortOrganizer(organizer)"
          >
            <span class="short-organizer-name">{{ shortOrganizerDisplay(organizer) }}</span>
            <span class="short-organizer-meta">{{ shortOrganizerMeta(organizer) }}</span>
          </button>

          <p v-if="!filteredShortOrganizers.length && !shortOrganizersLoading" class="co-organizer-empty modal-empty">
            No hay organizers disponibles para invitar.
          </p>
          <p v-if="shortOrganizersLoading" class="co-organizer-empty modal-empty">Cargando organizers…</p>
        </div>

        <div class="modal-buttons">
          <button
              type="button"
              class="btn btn-primary"
              :disabled="isBusy || !selectedShortOrganizer || !canAddMoreCoOrganizers"
              @click="inviteSelectedCoOrganizer"
          >
            <i class="fa-solid fa-user-plus"></i> Invitar
          </button>
          <button type="button" class="btn btn-secondary" :disabled="isBusy" @click="closeCoOrganizerModal">
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <div v-if="isLiveQrModalVisible" class="modal-backdrop" @click.self="closeLiveQrModal">
      <div class="modal-content live-qr-modal">
        <div class="live-qr-modal-head">
          <div>
            <p class="live-qr-strip-label">Live QR</p>
            <h3>Download branded QR pack</h3>
          </div>
          <button type="button" class="clear-selection-btn" :disabled="liveQrLoading" @click="closeLiveQrModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div v-if="liveQrTargetUrl" class="live-qr-modal-url-row">
          <p class="live-qr-modal-url">{{ liveQrTargetUrl }}</p>
          <button
              type="button"
              class="live-qr-copy-btn"
              :aria-label="$t('competitions.view.live_qr_copy')"
              :disabled="liveQrLoading"
              :title="$t('competitions.view.live_qr_copy')"
              @click="copyLiveQrUrl"
          >
            <i :class="liveQrCopyState === 'copied' ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
          </button>
        </div>
        <p v-if="liveQrError" class="co-organizer-feedback is-error">{{ liveQrError }}</p>
        <p v-else-if="liveQrLoading" class="live-qr-loading">Generating the light and dark QR images…</p>

        <div v-if="!liveQrLoading && (liveQrImages.light || liveQrImages.dark)" class="live-qr-grid">
          <article class="live-qr-card live-qr-card-light">
            <div class="live-qr-card-head">
              <h4>Light</h4>
              <button type="button" class="btn btn-primary btn-sm" aria-label="Download light QR" title="Download light QR" @click="downloadLiveQr('light')">
                <i class="fa-solid fa-download"></i>
              </button>
            </div>
            <div class="live-qr-preview-shell">
              <img v-if="liveQrImages.light?.dataUrl" :src="liveQrImages.light.dataUrl" alt="Live QR light" class="live-qr-preview">
            </div>
          </article>

          <article class="live-qr-card live-qr-card-dark">
            <div class="live-qr-card-head">
              <h4>Dark</h4>
              <button type="button" class="btn btn-light btn-sm" aria-label="Download dark QR" title="Download dark QR" @click="downloadLiveQr('dark')">
                <i class="fa-solid fa-download"></i>
              </button>
            </div>
            <div class="live-qr-preview-shell live-qr-preview-shell-dark">
              <img v-if="liveQrImages.dark?.dataUrl" :src="liveQrImages.dark.dataUrl" alt="Live QR dark" class="live-qr-preview">
            </div>
          </article>
        </div>
      </div>
    </div>

    <CompetitionStatisticsModal
        :visible="isCompetitionStatsModalVisible"
        :loading="competitionStatisticsLoading"
        :stats="competitionStatisticsPayload"
        :error="competitionStatisticsError"
        @close="closeCompetitionStatisticsModal"
    />
  </div>
</template>

<script>
import DetailItem from "@/components/competitions/DetailItem.vue";
import CompetitionStatisticsModal from "@/components/competitions/CompetitionStatisticsModal.vue";
import {
  formatCompetitionTime,
  getCompetitionDateParts,
  parseCompetitionDate,
  resolveCompetitionTimezone,
} from '@/helpers/timezoneDate';
import router from "@/router/router";
import lightQrLogo from "@/assets/logos/logo_tjs_mini_black.png";
import darkQrLogo from "@/assets/logos/logo_tjs_mini_white.png";
import { mapActions, mapState } from "vuex";

export default {
  name: "CompetitionDetails",
  components: { CompetitionStatisticsModal, DetailItem },
  props: { competition: { type: Object, required: true } },
  data() {
    return {
      isCoOrganizerModalVisible: false,
      isLiveQrModalVisible: false,
      isCompetitionStatsModalVisible: false,
      organizersShortList: [],
      organizerSearchQuery: "",
      selectedShortOrganizer: null,
      shortOrganizersLoading: false,
      shortOrganizersError: "",
      isBusy: false,
      liveQrLoading: false,
      liveQrError: "",
      liveQrCopyState: "idle",
      liveQrCopyTimeoutId: null,
      competitionStatisticsLoading: false,
      competitionStatisticsError: "",
      competitionStatisticsPayload: null,
      liveQrImages: {
        light: null,
        dark: null,
      },
      coOrganizerFeedback: {
        type: "success",
        message: "",
      },
    };
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    competitionStatusId() {
      return Number(this.competition?.status_id ?? 0);
    },
    isCreatorOrganizer() {
      const userId = Number(this.user?.id ?? this.user?.user?.id);
      const organizerId = Number(this.competition?.organizer_id ?? this.competition?.organizer?.id);
      if (!Number.isFinite(userId) || !Number.isFinite(organizerId)) return false;
      return userId === organizerId;
    },
    isCoOrganizerActionsLocked() {
      return [8, 15].includes(this.competitionStatusId);
    },
    isCoOrganizerManagementWindowOpen() {
      return !this.isCoOrganizerActionsLocked && this.competitionStatusId >= 10 && this.competitionStatusId < 15;
    },
    isLiveActionsAvailable() {
      return this.competitionStatusId >= 4;
    },
    formattedDate() {
      const startRaw = this.competition.competitions_info?.date_start;
      const endRaw = this.competition.competitions_info?.date_end;
      if (!startRaw) return this.$t('competitions.view.not_defined');

      const competitionTimeZone = resolveCompetitionTimezone(this.competition);
      const start = parseCompetitionDate(startRaw, competitionTimeZone);
      const end = endRaw ? parseCompetitionDate(endRaw, competitionTimeZone) : null;
      if (!start || Number.isNaN(start.getTime())) return this.$t('competitions.view.not_defined');

      const startParts = getCompetitionDateParts(start, competitionTimeZone);
      const endParts = end ? getCompetitionDateParts(end, competitionTimeZone) : null;
      if (!startParts) return this.$t('competitions.view.not_defined');

      const pad = (n) => String(n).padStart(2, "0");
      const formatFull = (parts) => `${pad(parts.day)}/${pad(parts.month)}/${String(parts.year).slice(-2)}`;

      const hasValidEnd = end && endParts && !Number.isNaN(end.getTime());
      if (hasValidEnd) {
        const sameY = startParts.year === endParts.year;
        const sameM = startParts.month === endParts.month;
        const sameD = startParts.day === endParts.day;
        if (sameY && sameM && sameD) {
          return formatFull(startParts);
        }
        if (sameY && sameM) {
          return `${pad(startParts.day)}-${pad(endParts.day)}/${pad(endParts.month)}/${String(endParts.year).slice(-2)}`;
        }
        return `${formatFull(startParts)} - ${formatFull(endParts)}`;
      }

      return formatFull(startParts);
    },
    formattedTime() {
      const startRaw = this.competition.competitions_info?.date_start;
      const endRaw = this.competition.competitions_info?.date_end;
      if (!startRaw) return this.$t('competitions.view.not_defined');

      const competitionTimeZone = resolveCompetitionTimezone(this.competition);
      const start = parseCompetitionDate(startRaw, competitionTimeZone);
      const end = endRaw ? parseCompetitionDate(endRaw, competitionTimeZone) : null;
      if (!start || Number.isNaN(start.getTime())) return this.$t('competitions.view.not_defined');

      const startTime = formatCompetitionTime(start, competitionTimeZone, 'es-ES');
      const endValid = end && !Number.isNaN(end.getTime());

      if (endValid) {
        const endTime = formatCompetitionTime(end, competitionTimeZone, 'es-ES');
        if (startTime === endTime) return startTime;
        return `${startTime} - ${endTime}`;
      }

      return startTime;
    },
    formattedQualification() {
      let response = this.$t('competitions.view.not_defined');
      const qualification = this.competition.is_qualification_dual;
      if(qualification){
        response = 2;
      }else if(qualification === 0){
        response = 1;
      }
      return response;
    },
    formattedIsOpen() {
      let response = this.$t('competitions.view.not_defined');
      const is_open = this.competition.is_open;
      if(is_open){
        response = this.$t('competitions.view.open');
      }else if(is_open === 0){
        response = this.$t('competitions.view.close');
      }
      return response;
    },
    formattedIsVs() {
      let response = this.$t('competitions.view.not_defined');
      const is_vs = this.competition.is_vs;
      if(is_vs){
        response = 'Vs';
      }else if(is_vs === 0){
        response = 'Scoring';
      }
      return response;
    },
    normalizedCoOrganizers() {
      const directList = Array.isArray(this.competition?.co_organizers) ? this.competition.co_organizers : [];
      const competitionUsers = Array.isArray(this.competition?.competition_users)
        ? this.competition.competition_users.filter(user => Number(user?.role_id ?? user?.pivot?.role_id) === 3)
        : [];

      const merged = new Map();
      [...directList, ...competitionUsers].forEach(item => {
        if (!item?.id) return;
        merged.set(Number(item.id), {
          ...(merged.get(Number(item.id)) || {}),
          ...item,
          user: {
            ...(merged.get(Number(item.id))?.user || {}),
            ...(item.user || {}),
          },
          status_id: Number(item?.status_id ?? item?.status?.id ?? item?.pivot?.status_id ?? 0),
          user_id: Number(item?.user_id ?? item?.user?.id ?? 0),
        });
      });

      return Array.from(merged.values());
    },
    sortedCoOrganizers() {
      const order = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 };
      return [...this.normalizedCoOrganizers].sort((a, b) => {
        const statusDiff = (order[Number(a.status_id)] || 99) - (order[Number(b.status_id)] || 99);
        if (statusDiff !== 0) return statusDiff;
        return Number(b.id) - Number(a.id);
      });
    },
    occupiedCoOrganizerSlots() {
      return this.normalizedCoOrganizers.filter(item => this.isOccupiedStatus(item.status_id)).length;
    },
    visibleCoOrganizers() {
      if (this.isCreatorOrganizer) {
        return this.sortedCoOrganizers;
      }

      const currentUserId = Number(this.user?.id ?? this.user?.user?.id ?? 0);

      return this.sortedCoOrganizers.filter(item => {
        const statusId = Number(item.status_id);
        const coOrganizerUserId = Number(item.user_id ?? item.user?.id ?? 0);

        return statusId === 4 || (currentUserId > 0 && coOrganizerUserId === currentUserId);
      });
    },
    visibleCoOrganizerSlots() {
      return this.visibleCoOrganizers.filter(item => this.isOccupiedStatus(item.status_id)).length;
    },
    canAddMoreCoOrganizers() {
      return this.occupiedCoOrganizerSlots < 2;
    },
    canManageCoOrganizers() {
      return this.isCreatorOrganizer;
    },
    canOpenCoOrganizerModal() {
      return this.canManageCoOrganizers && this.isCoOrganizerManagementWindowOpen && this.canAddMoreCoOrganizers;
    },
    coOrganizerAddButtonTitle() {
      if (!this.canManageCoOrganizers) return 'Solo el organizer creador puede gestionar co-organizadores';
      if (!this.isCoOrganizerManagementWindowOpen) return 'Los co-organizadores solo se gestionan antes del inicio de la competición';
      if (!this.canAddMoreCoOrganizers) return 'Límite de 2 co-organizadores alcanzado';
      return 'Añadir co-organizador';
    },
    showCoOrganizerBlock() {
      if (this.isCreatorOrganizer) {
        return this.isCoOrganizerManagementWindowOpen || this.visibleCoOrganizers.length > 0;
      }

      return this.visibleCoOrganizers.length > 0;
    },
    occupiedCoOrganizerUserIds() {
      return new Set(
        this.normalizedCoOrganizers
          .filter(item => this.isOccupiedStatus(item.status_id))
          .map(item => Number(item.user_id ?? item.user?.id))
          .filter(Boolean)
      );
    },
    filteredShortOrganizers() {
      const query = this.organizerSearchQuery.trim().toLowerCase();
      return (this.organizersShortList || []).filter(organizer => {
        const organizerId = Number(organizer.id);
        if (!organizerId) return false;
        if (organizerId === Number(this.competition?.organizer_id)) return false;
        if (this.occupiedCoOrganizerUserIds.has(organizerId)) return false;

        const haystack = [organizer.name, organizer.surname, organizer.username, organizer.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return !query || haystack.includes(query);
      });
    },
    selectedOrganizerLabel() {
      return this.selectedShortOrganizer ? this.shortOrganizerDisplay(this.selectedShortOrganizer) : '';
    },
    liveQrConfig() {
      return this.competition?.live_qr || null;
    },
    livePublicUrl() {
      return this.liveQrConfig?.target_url || this.competition?.live_channel?.public_url || '';
    },
    liveChannelCode() {
      return this.liveQrConfig?.channel_code || this.competition?.live_channel?.channel_code || this.competition?.channel_code || '';
    },
    showLiveQrBlock() {
      return this.isLiveActionsAvailable && this.liveActionsCount > 0;
    },
    showLiveQrButton() {
      return this.isLiveActionsAvailable && Boolean(this.liveQrConfig);
    },
    showCompetitionStatisticsButton() {
      return this.isLiveActionsAvailable && Boolean(this.competition?.id);
    },
    liveActionsCount() {
      return [this.liveFollowAvailable, this.showLiveQrButton, this.showCompetitionStatisticsButton].filter(Boolean).length;
    },
    hasMultipleLiveActions() {
      return this.liveActionsCount > 1;
    },
    liveFollowAvailable() {
      return this.isLiveActionsAvailable && Boolean(this.livePublicUrl);
    },
    liveFollowButtonTitle() {
      if (this.liveFollowAvailable) {
        return 'Abrir canal live público';
      }

      if (!this.liveChannelCode) {
        return 'Esta competición todavía no tiene un canal live generado.';
      }

      return 'La URL pública del canal live todavía no está disponible.';
    },
    canGenerateLiveQr() {
      return Boolean(this.liveQrConfig?.available && this.liveQrConfig?.target_url);
    },
    liveQrDisabledReason() {
      if (!this.liveQrConfig) {
        return 'El QR live solo está disponible para el organizer y los co-organizers aprobados.';
      }

      if (!this.liveChannelCode) {
        return 'Esta competición todavía no tiene un canal live generado.';
      }

      return 'El QR no está disponible porque la URL pública del canal live todavía no existe.';
    },
    liveQrButtonTitle() {
      if (this.liveQrLoading) {
        return this.$t('competitions.view.live_qr_generating');
      }

      if (this.canGenerateLiveQr) {
        return this.$t('competitions.view.live_qr_button');
      }

      return this.liveQrDisabledReason;
    },
    liveQrTargetUrl() {
      return this.liveQrConfig?.target_url || this.livePublicUrl || '';
    },
    competitionStatisticsButtonTitle() {
      if (this.competitionStatisticsLoading) {
        return this.tOr('profile.stats.loading', 'Loading statistics...');
      }

      return this.tOr('menu.statistics', 'Statistics');
    }
  },
  watch: {
    'competition.id'() {
      this.resetLiveQrState();
      this.resetCompetitionStatisticsState();
    }
  },
  methods:{
    ...mapActions('competition', ['setCompetition']),
    ...mapActions('competition_users', ['setCompetitionUsers']),
    controlIconCategory(category) {
      switch (category) {
        case "nacional":
          return 'fa-solid fa-flag';
        case "international":
          return 'fa-solid fa-earth-europe';
        case "team":
          return 'fa-solid fa-users';
        default:
          return 'fa-solid fa-question-circle'; // fallback si no se reconoce
      }
    },
    controlValueCategory(category){
      switch (category) {
        case "national":
          return 'National';
        case "international":
          return 'International';
        case "team":
          return 'Team';
        default:
          return ''; // fallback si no se reconoce
      }
    },
    controlIconGender(gender) {
      switch (gender) {
        case "male":
          return 'fa-solid fa-person';
        case "female":
          return 'fa-solid fa-venus-mars';
        case "mixed":
          return 'fa-solid fa-people-arrows';
        default:
          return 'fa-solid fa-question-circle'; // fallback si no se reconoce
      }
    },
    controlValueGender(gender){
      switch (gender) {
        case "male":
          return 'Male';
        case "female":
          return 'Famale';
        case "mixed":
          return 'Mixed';
        default:
          return this.$t('competitions.view.not_defined'); // fallback si no se reconoce
      }
    },
    tOr(key, fallback) {
      if (typeof this.$te === 'function' && this.$te(key)) {
        return this.$t(key);
      }

      return fallback;
    },
    viewOrganizer(id) {
      router.push(`/profile/${id}`);
    },
    shortOrganizerDisplay(organizer) {
      return [organizer?.name, organizer?.surname].filter(Boolean).join(' ').trim() || organizer?.username || organizer?.email || 'Organizer';
    },
    shortOrganizerMeta(organizer) {
      const meta = [];
      if (organizer?.username) meta.push(`@${organizer.username}`);
      if (organizer?.email) meta.push(organizer.email);
      return meta.join(' · ');
    },
    fullName(user, fallbackEmail = '') {
      const fullName = [user?.name, user?.surname].filter(Boolean).join(' ').trim();
      return fullName || user?.username || user?.email || fallbackEmail || 'Sin nombre';
    },
    isOccupiedStatus(statusId) {
      return [1, 2, 3, 4].includes(Number(statusId));
    },
    statusIcon(statusId) {
      switch (Number(statusId)) {
        case 1:
          return 'fa-solid fa-clock';
        case 2:
          return 'fa-solid fa-paper-plane';
        case 3:
          return 'fa-solid fa-hourglass-half';
        case 4:
          return 'fa-solid fa-circle-check';
        case 5:
          return 'fa-solid fa-ban';
        default:
          return 'fa-solid fa-circle-question';
      }
    },
    statusBadgeClass(statusId) {
      switch (Number(statusId)) {
        case 1:
          return 'is-created';
        case 2:
          return 'is-invited';
        case 3:
          return 'is-pending';
        case 4:
          return 'is-approved';
        case 5:
          return 'is-rejected';
        default:
          return 'is-unknown';
      }
    },
    coOrganizerStatusLabel(statusId) {
      const normalizedStatusId = Number(statusId);
      if ([1, 2, 3, 4, 5].includes(normalizedStatusId)) {
        return this.$t(`rules.status_${normalizedStatusId}`);
      }
      return this.$t('competitions.view.not_defined');
    },
    canSendInvitation(coOrganizer) {
      return this.canManageCoOrganizers && this.isCoOrganizerManagementWindowOpen && Number(coOrganizer?.status_id) === 1;
    },
    canAcceptInvitation(coOrganizer) {
      const isCurrentUser = Number(this.user?.id) === Number(coOrganizer?.user_id ?? coOrganizer?.user?.id);
      return this.isCoOrganizerManagementWindowOpen && isCurrentUser && [2].includes(Number(coOrganizer?.status_id));
    },
    canApproveCoOrganizer(coOrganizer) {
      return this.canManageCoOrganizers && this.isCoOrganizerManagementWindowOpen && Number(coOrganizer?.status_id) === 3;
    },
    canRejectInvitation(coOrganizer) {
      if (!this.isCoOrganizerManagementWindowOpen) return false;
      const statusId = Number(coOrganizer?.status_id);
      const isCurrentUser = Number(this.user?.id) === Number(coOrganizer?.user_id ?? coOrganizer?.user?.id);
      return (this.canManageCoOrganizers && [2, 3, 4].includes(statusId)) || (isCurrentUser && [2, 3].includes(statusId));
    },
    clearFeedback() {
      this.coOrganizerFeedback = { type: 'success', message: '' };
    },
    setFeedback(type, message) {
      this.coOrganizerFeedback = { type, message };
    },
    onOrganizerSearchInput() {
      if (!this.selectedShortOrganizer) return;
      if (this.organizerSearchQuery !== this.shortOrganizerDisplay(this.selectedShortOrganizer)) {
        this.selectedShortOrganizer = null;
      }
    },
    selectShortOrganizer(organizer) {
      this.selectedShortOrganizer = organizer;
      this.organizerSearchQuery = this.shortOrganizerDisplay(organizer);
    },
    clearSelectedOrganizer() {
      this.selectedShortOrganizer = null;
      this.organizerSearchQuery = '';
    },
    async openCoOrganizerModal() {
      if (!this.canOpenCoOrganizerModal) return;
      this.clearFeedback();
      this.shortOrganizersError = '';
      this.isCoOrganizerModalVisible = true;
      this.clearSelectedOrganizer();
      await this.fetchShortOrganizers();
    },
    closeCoOrganizerModal() {
      if (this.isBusy) return;
      this.isCoOrganizerModalVisible = false;
      this.organizerSearchQuery = '';
      this.selectedShortOrganizer = null;
      this.shortOrganizersError = '';
    },
    async fetchShortOrganizers() {
      this.shortOrganizersLoading = true;
      this.shortOrganizersError = '';
      const endpoints = ['/organizers/short', '/organizers/list_short'];
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          const response = await this.$axios.get(endpoint, {
            headers: { Authorization: `Bearer ${this.token}` },
          });
          const users = response?.data?.users || response?.data?.organizers || [];
          this.organizersShortList = Array.isArray(users) ? users : [];
          this.shortOrganizersLoading = false;
          return;
        } catch (error) {
          lastError = error;
          if (error?.response?.status !== 404) {
            break;
          }
        }
      }

      this.organizersShortList = [];
      this.shortOrganizersError = lastError?.response?.data?.message || 'No se pudo cargar el listado corto de organizers.';
      this.shortOrganizersLoading = false;
    },
    async refreshCompetitionData() {
      const competitionId = Number(this.competition?.id);
      if (!competitionId) return null;

      const response = await this.$axios.get(`/competition/${competitionId}`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });

      if (response.status === 200 && response.data?.competition) {
        await this.setCompetition(response.data.competition);
        await this.setCompetitionUsers(response.data.competition?.competition_users || []);
        return response.data.competition;
      }

      return null;
    },
    async inviteSelectedCoOrganizer() {
      if (!this.canOpenCoOrganizerModal) {
        this.shortOrganizersError = this.coOrganizerAddButtonTitle;
        return;
      }
      if (!this.selectedShortOrganizer) {
        this.shortOrganizersError = 'Selecciona un organizer.';
        return;
      }
      if (!this.canAddMoreCoOrganizers) {
        this.shortOrganizersError = 'Solo se permiten 2 co-organizadores por competición.';
        return;
      }

      this.isBusy = true;
      this.shortOrganizersError = '';
      this.clearFeedback();

      const selectedId = Number(this.selectedShortOrganizer.id);
      let shouldCloseModal = false;

      try {
        const payload = {
          competition_id: this.competition.id,
          co_organizers_list: [selectedId],
          organizers_list: [selectedId],
        };
        let response;

        try {
          response = await this.$axios.post('/organizers/invitation', payload, {
            headers: { Authorization: `Bearer ${this.token}` },
          });
        } catch (error) {
          if (error?.response?.status !== 404) throw error;
          response = await this.$axios.post('/organizers/invitation', payload, {
            headers: { Authorization: `Bearer ${this.token}` },
          });
        }

        if (!response?.data?.success) {
          this.shortOrganizersError = response?.data?.message || 'No se pudo añadir el co-organizador.';
          return;
        }

        const updatedCompetition = await this.refreshCompetitionData();
        const createdCandidate = (updatedCompetition?.co_organizers || this.normalizedCoOrganizers || [])
          .filter(item => Number(item?.user_id ?? item?.user?.id) === selectedId && Number(item?.status_id ?? item?.status?.id) === 1)
          .sort((a, b) => Number(b.id) - Number(a.id))[0];

        if (createdCandidate?.id) {
          await this.updateCoOrganizerStatus(createdCandidate, 2, { keepBusy: true, keepFeedback: true });
          this.setFeedback('success', this.$t('competitions.view.co_organizer_invited_success'));
        } else {
          this.setFeedback('success', response?.data?.message || this.$t('competitions.view.co_organizer_added_success'));
        }

        shouldCloseModal = true;
      } catch (error) {
        this.shortOrganizersError = error?.response?.data?.message || 'No se pudo invitar al co-organizador.';
      } finally {
        this.isBusy = false;
        if (shouldCloseModal) {
          this.closeCoOrganizerModal();
        }
      }
    },
    async updateCoOrganizerStatus(coOrganizer, statusId, options = {}) {
      const { keepBusy = false, keepFeedback = false } = options;
      if (!coOrganizer?.id || !this.competition?.id) return;

      const nextStatusId = Number(statusId);
      const isCurrentUser = Number(this.user?.id) === Number(coOrganizer?.user_id ?? coOrganizer?.user?.id);
      const currentStatusId = Number(coOrganizer?.status_id);
      const canExecuteAction = (
        (this.isCreatorOrganizer && this.isCoOrganizerManagementWindowOpen && (
          (nextStatusId === 2 && currentStatusId === 1)
          || (nextStatusId === 4 && currentStatusId === 3)
          || (nextStatusId === 5 && [2, 3, 4].includes(currentStatusId))
        ))
        || (isCurrentUser && this.isCoOrganizerManagementWindowOpen && (
          (nextStatusId === 3 && currentStatusId === 2)
          || (nextStatusId === 5 && [2, 3].includes(currentStatusId))
        ))
      );

      if (!canExecuteAction) {
        if (!keepFeedback) {
          this.setFeedback('error', 'No tienes permisos para actualizar este co-organizador en el estado actual de la competición.');
        }
        return;
      }

      if (!keepBusy) {
        this.isBusy = true;
      }
      if (!keepFeedback) {
        this.clearFeedback();
      }

      try {
        const response = await this.$axios.post(
          '/status/update',
          {
            entity: 'organizers',
            competition_id: this.competition.id,
            competition_user_id: coOrganizer.id,
            status_id: statusId,
            id: coOrganizer.id,
            new_status: statusId,
          },
          {
            headers: { Authorization: `Bearer ${this.token}` },
          }
        );

        if (!response?.data?.success) {
          this.setFeedback('error', response?.data?.message || 'No se pudo actualizar el estado del co-organizador.');
          return;
        }

        await this.refreshCompetitionData();

        if (!keepFeedback) {
          this.setFeedback('success', this.successMessageByStatus(statusId));
        }
      } catch (error) {
        this.setFeedback('error', error?.response?.data?.message || 'No se pudo actualizar el estado del co-organizador.');
      } finally {
        if (!keepBusy) {
          this.isBusy = false;
        }
      }
    },
    successMessageByStatus(statusId) {
      switch (Number(statusId)) {
        case 2:
          return this.$t('competitions.view.co_organizer_invitation_sent');
        case 3:
          return this.$t('competitions.view.co_organizer_invitation_accepted');
        case 4:
          return this.$t('competitions.view.co_organizer_approved');
        case 5:
          return this.$t('competitions.view.co_organizer_status_updated');
        default:
          return this.$t('competitions.view.co_organizer_status_updated');
      }
    },
    resetLiveQrState() {
      this.isLiveQrModalVisible = false;
      this.liveQrLoading = false;
      this.liveQrError = '';
      this.liveQrCopyState = 'idle';
      if (this.liveQrCopyTimeoutId) {
        clearTimeout(this.liveQrCopyTimeoutId);
        this.liveQrCopyTimeoutId = null;
      }
      this.liveQrImages = {
        light: null,
        dark: null,
      };
    },
    async openLiveQrModal() {
      this.isLiveQrModalVisible = true;
      this.liveQrError = '';
      this.liveQrCopyState = 'idle';

      if (this.liveQrCopyTimeoutId) {
        clearTimeout(this.liveQrCopyTimeoutId);
        this.liveQrCopyTimeoutId = null;
      }

      if (!this.canGenerateLiveQr) {
        this.liveQrError = 'The live channel URL is not available yet for this competition.';
        return;
      }

      if (this.liveQrImages.light && this.liveQrImages.dark) {
        return;
      }

      this.liveQrLoading = true;

      try {
        const [lightBlob, darkBlob] = await Promise.all([
          this.fetchLiveQrSource(this.liveQrConfig?.variants?.light?.source_url),
          this.fetchLiveQrSource(this.liveQrConfig?.variants?.dark?.source_url),
        ]);

        const [lightDataUrl, darkDataUrl] = await Promise.all([
          this.composeLiveQrVariant(lightBlob, 'light'),
          this.composeLiveQrVariant(darkBlob, 'dark'),
        ]);

        this.liveQrImages = {
          light: {
            dataUrl: lightDataUrl,
            filename: this.liveQrConfig?.variants?.light?.filename || `competition-${this.competition?.id}-live-qr-light.png`,
          },
          dark: {
            dataUrl: darkDataUrl,
            filename: this.liveQrConfig?.variants?.dark?.filename || `competition-${this.competition?.id}-live-qr-dark.png`,
          },
        };
      } catch (error) {
        this.liveQrError = error?.response?.data?.message || error?.message || 'Unable to generate the live QR images.';
      } finally {
        this.liveQrLoading = false;
      }
    },
    closeLiveQrModal() {
      if (this.liveQrLoading) return;
      this.isLiveQrModalVisible = false;
      this.liveQrCopyState = 'idle';
      if (this.liveQrCopyTimeoutId) {
        clearTimeout(this.liveQrCopyTimeoutId);
        this.liveQrCopyTimeoutId = null;
      }
    },
    async copyLiveQrUrl() {
      if (!this.liveQrTargetUrl) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.liveQrTargetUrl);
        } else {
          const input = document.createElement('input');
          input.value = this.liveQrTargetUrl;
          input.setAttribute('readonly', 'readonly');
          input.style.position = 'absolute';
          input.style.left = '-9999px';
          document.body.appendChild(input);
          input.select();
          const copied = document.execCommand('copy');
          document.body.removeChild(input);
          if (!copied) {
            throw new Error('Unable to copy the link.');
          }
        }

        this.liveQrCopyState = 'copied';
        this.liveQrError = '';
        if (this.liveQrCopyTimeoutId) {
          clearTimeout(this.liveQrCopyTimeoutId);
        }
        this.liveQrCopyTimeoutId = setTimeout(() => {
          this.liveQrCopyState = 'idle';
          this.liveQrCopyTimeoutId = null;
        }, 1800);
      } catch (error) {
        this.liveQrError = this.$t('competitions.view.live_qr_copy_error');
      }
    },
    async fetchLiveQrSource(sourceUrl) {
      if (!sourceUrl) {
        throw new Error('Missing QR source URL.');
      }

      const response = await this.$axios.get(sourceUrl, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${this.token}` },
      });

      return response.data;
    },
    async composeLiveQrVariant(blob, theme) {
      const qrImage = await this.loadImageFromObject(blob);
      const logoImage = await this.loadImage(theme === 'dark' ? darkQrLogo : lightQrLogo);
      const canvas = document.createElement('canvas');
      const size = 1200;
      const qrInset = 64;
      const plateSize = 220;
      const logoMaxWidth = 132;
      const logoMaxHeight = 132;
      const plateX = (size - plateSize) / 2;
      const plateY = (size - plateSize) / 2;
      const context = canvas.getContext('2d');

      canvas.width = size;
      canvas.height = size;

      context.fillStyle = theme === 'dark' ? '#0f172a' : '#ffffff';
      context.fillRect(0, 0, size, size);
      context.drawImage(qrImage, qrInset, qrInset, size - qrInset * 2, size - qrInset * 2);

      context.save();
      context.shadowColor = theme === 'dark' ? 'rgba(15, 23, 42, 0.42)' : 'rgba(15, 23, 42, 0.16)';
      context.shadowBlur = 20;
      context.shadowOffsetY = 10;
      context.fillStyle = theme === 'dark' ? '#111827' : '#ffffff';
      this.drawRoundedRect(context, plateX, plateY, plateSize, plateSize, 32);
      context.fill();
      context.restore();

      context.save();
      context.beginPath();
      context.arc(size / 2, size / 2, 74, 0, Math.PI * 2);
      context.closePath();
      context.fillStyle = theme === 'dark' ? '#111827' : '#ffffff';
      context.fill();
      context.restore();

      const fittedLogo = this.fitImageWithinBox(
        logoImage.width,
        logoImage.height,
        logoMaxWidth,
        logoMaxHeight
      );

      context.drawImage(
        logoImage,
        (size - fittedLogo.width) / 2,
        (size - fittedLogo.height) / 2,
        fittedLogo.width,
        fittedLogo.height
      );

      return canvas.toDataURL('image/png');
    },
    fitImageWithinBox(sourceWidth, sourceHeight, maxWidth, maxHeight) {
      if (!sourceWidth || !sourceHeight) {
        return {
          width: maxWidth,
          height: maxHeight,
        };
      }

      const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);

      return {
        width: Math.round(sourceWidth * ratio),
        height: Math.round(sourceHeight * ratio),
      };
    },
    drawRoundedRect(context, x, y, width, height, radius) {
      const safeRadius = Math.min(radius, width / 2, height / 2);
      context.beginPath();
      context.moveTo(x + safeRadius, y);
      context.lineTo(x + width - safeRadius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
      context.lineTo(x + width, y + height - safeRadius);
      context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
      context.lineTo(x + safeRadius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
      context.lineTo(x, y + safeRadius);
      context.quadraticCurveTo(x, y, x + safeRadius, y);
      context.closePath();
    },
    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Unable to load image asset.'));
        image.src = src;
      });
    },
    loadImageFromObject(blob) {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(image);
        };
        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Unable to load QR source image.'));
        };
        image.src = objectUrl;
      });
    },
    downloadLiveQr(theme) {
      const asset = this.liveQrImages?.[theme];
      if (!asset?.dataUrl) return;

      const anchor = document.createElement('a');
      anchor.href = asset.dataUrl;
      anchor.download = asset.filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    resetCompetitionStatisticsState() {
      this.isCompetitionStatsModalVisible = false;
      this.competitionStatisticsLoading = false;
      this.competitionStatisticsError = '';
      this.competitionStatisticsPayload = null;
    },
    async openCompetitionStatisticsModal() {
      const competitionId = Number(this.competition?.id);
      if (!competitionId) {
        return;
      }

      this.isCompetitionStatsModalVisible = true;

      if (this.competitionStatisticsPayload || this.competitionStatisticsLoading) {
        return;
      }

      this.competitionStatisticsLoading = true;
      this.competitionStatisticsError = '';

      try {
        const response = await this.$axios.get(`/competitions/${competitionId}/statistics`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        this.competitionStatisticsPayload = response?.data?.statistics || null;
      } catch (error) {
        this.competitionStatisticsError = error?.response?.data?.message || 'No se pudieron cargar las estadísticas de la competición.';
      } finally {
        this.competitionStatisticsLoading = false;
      }
    },
    closeCompetitionStatisticsModal() {
      if (this.competitionStatisticsLoading) return;
      this.isCompetitionStatsModalVisible = false;
    },
  }
};
</script>

<style scoped>
.competition-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); /* 2 columnas en pantallas grandes, 1 en móviles */
  gap: 12px;
  padding: 14px; /* reduce padding to free space for carousel */
}

.details-column {
  display: flex;
  padding: 8px 12px; /* tighter padding for more content width */
  justify-content: center;
}

.details-column-inner {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  text-align: left;
}

.live-qr-strip {
  margin: 0 14px 14px;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(237, 242, 247, 0.92) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.live-qr-strip-actions {
  width: min(100%, 960px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin: 0 auto;
  flex-wrap: wrap;
}

.live-qr-strip-actions.has-multiple {
  justify-content: center;
}

.live-qr-strip-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.live-qr-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  border: none;
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.live-qr-action:hover {
  color: #ffffff;
  background: #111827;
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
}

.live-qr-action:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.25);
  outline-offset: 2px;
}

.live-qr-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.live-qr-follow-link {
  margin-top: 2px;
  width: fit-content;
}

.live-qr-follow-link:hover {
  text-decoration: none;
}

.live-qr-modal-url-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.live-qr-modal-url {
  margin: 0;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  font-size: 13px;
  color: #475569;
  word-break: break-all;
}

.live-qr-copy-btn {
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.live-qr-copy-btn:hover {
  background: #111827;
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.18);
}

.live-qr-copy-btn:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.25);
  outline-offset: 2px;
}

.live-qr-copy-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.live-qr-trigger {
  gap: 10px;
}

.live-qr-modal {
  width: min(860px, calc(100vw - 32px));
  max-height: calc(100vh - 40px);
  overflow: auto;
}

.live-qr-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.live-qr-modal-head h3 {
  margin: 4px 0 0;
}

.live-qr-loading {
  margin: 18px 0 0;
  color: #475569;
}

.live-qr-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.live-qr-card {
  border-radius: 24px;
  padding: 14px;
}

.live-qr-card-light {
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.live-qr-card-dark {
  background: #0f172a;
  color: #ffffff;
}

.live-qr-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.live-qr-card-head h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.live-qr-preview-shell {
  border-radius: 18px;
  background: #ffffff;
  padding: 10px;
}

.live-qr-preview-shell-dark {
  background: rgba(255, 255, 255, 0.08);
}

.live-qr-preview {
  width: 100%;
  max-width: 280px;
  display: block;
  border-radius: 16px;
  margin: 0 auto;
}
.link-plain {
  text-decoration: underline;
  border: none;
  outline: none;
  color: inherit;
  background: none;
}

.organizer-section {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.organizer-link {
  flex: 1;
}

.co-organizer-add-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  background: #1b5bbf;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
}

.co-organizer-add-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .live-qr-strip {
    align-items: stretch;
  }

  .live-qr-strip-actions,
  .live-qr-strip-actions.has-multiple {
    width: 100%;
    flex-direction: column;
    justify-content: center;
  }

  .live-qr-modal-url-row {
    flex-direction: column;
    align-items: stretch;
  }

  .live-qr-trigger {
    justify-content: center;
  }

  .live-qr-copy-btn {
    justify-content: center;
  }

  .live-qr-grid {
    grid-template-columns: 1fr;
  }
}

.co-organizers-inline {
  margin-left: 24px;
  margin-top: -2px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.co-organizers-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.co-organizers-title {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.co-organizers-counter {
  font-size: 12px;
  color: #6b7280;
}

.co-organizer-feedback {
  margin: 0;
  font-size: 12px;
}

.co-organizer-feedback.is-success {
  color: #0f8a43;
}

.co-organizer-feedback.is-error {
  color: #c62828;
}

.co-organizer-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.co-organizer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.co-organizer-content {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.co-organizer-name-btn {
  border: none;
  background: none;
  color: #1f2937;
  padding: 0;
  margin: 0;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.co-organizer-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.co-organizer-status.is-created {
  color: #8a4b08;
  background: #fff3cd;
}

.co-organizer-status.is-invited {
  color: #0b5394;
  background: #d9ecff;
}

.co-organizer-status.is-pending {
  color: #7c3f00;
  background: #ffe8cc;
}

.co-organizer-status.is-approved {
  color: #146c43;
  background: #d1e7dd;
}

.co-organizer-status.is-rejected {
  color: #842029;
  background: #f8d7da;
}

.co-organizer-status.is-unknown {
  color: #495057;
  background: #e9ecef;
}

.co-organizer-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.mini-action {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
}

.mini-action.send {
  background: #d9ecff;
  color: #0b5394;
}

.mini-action.approve {
  background: #d1e7dd;
  color: #146c43;
}

.mini-action.reject {
  background: #f8d7da;
  color: #842029;
}

.mini-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.co-organizer-empty {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  width: 50%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.co-organizer-modal {
  width: min(560px, 95%);
}

.selected-organizer-box {
  margin-bottom: 12px;
  text-align: left;
}

.selected-organizer-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.selected-organizer-input {
  display: flex;
  gap: 8px;
}

.clear-selection-btn {
  width: 38px;
  border: none;
  border-radius: 6px;
  background: #e9ecef;
  color: #495057;
}

.search-bar {
  margin-bottom: 12px;
}

.short-organizers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: 45vh;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.short-organizer-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 10px 12px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.short-organizer-item.selected {
  border-color: #1b5bbf;
  background: #eef5ff;
}

.short-organizer-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.short-organizer-meta {
  font-size: 12px;
  color: #6b7280;
}

.modal-empty {
  padding: 12px 0;
}

.modal-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

.btn {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background: #28a745;
  color: white;
}

.btn-secondary {
  background: #ccc;
}

/* Opcional: Ajustes para pantallas más pequeñas */
@media (max-width: 600px) {
  .competition-details-grid {
    grid-template-columns: 1fr; /* Solo 1 columna en móviles */
    padding: 6px; /* libera más ancho en móviles */
  }
  .details-column {
    padding: 6px 10px;
  }
  .co-organizers-inline {
    margin-left: 0;
  }
  .co-organizer-row {
    flex-direction: column;
    align-items: stretch;
  }
  .co-organizer-actions {
    justify-content: flex-start;
  }
  .round-sizes-wrapper {
    margin-left: -4px;
    margin-right: -4px;
  }
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
  }
}
</style>
